import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Zap, BarChart3, Mail, Send, Sparkles, X, Cpu } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Automations() {
  const [workflows, setWorkflows] = useState([]);
  const [statsWf, setStatsWf] = useState(null);
  const [stats, setStats] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [seqOpen, setSeqOpen] = useState(false);
  const [seqForm, setSeqForm] = useState({ goal: "", audience: "", tone: "warm and direct", name: "" });
  const [seqGenerating, setSeqGenerating] = useState(false);
  const [seqPreview, setSeqPreview] = useState(null); // {workflow_id, name, emails: [...]}
  const [autoTriage, setAutoTriage] = useState(false);
  const [triageThreshold, setTriageThreshold] = useState(0);
  const [triageBusy, setTriageBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/workflows");
      setWorkflows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAutoTriage(!!data.auto_triage_enabled);
      setTriageThreshold(Number(data.auto_triage_confidence_threshold || 0));
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); loadSettings(); }, []);

  const toggleAutoTriage = async () => {
    setTriageBusy(true);
    try {
      const next = !autoTriage;
      const { data } = await api.patch("/auth/me/settings", { auto_triage_enabled: next });
      setAutoTriage(!!data.auto_triage_enabled);
      toast.success(next ? "Auto-triage ON — new contacts will be enrolled automatically" : "Auto-triage OFF");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTriageBusy(false); }
  };
  const saveThreshold = async (val) => {
    try {
      const { data } = await api.patch("/auth/me/settings", { auto_triage_confidence_threshold: val });
      setTriageThreshold(Number(data.auto_triage_confidence_threshold || 0));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/workflows", {
        name: "Untitled workflow",
        enabled: false,
        trigger: { type: "contact_created", filters: {} },
        actions: [],
      });
      navigate(`/automations/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this workflow?")) return;
    try {
      await api.delete(`/workflows/${id}`);
      setWorkflows((w) => w.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (wf) => {
    try {
      const { data } = await api.put(`/workflows/${wf.id}`, { ...wf, enabled: !wf.enabled });
      setWorkflows((w) => w.map((x) => x.id === wf.id ? data : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hasAB = (wf) => (wf.actions || []).some((a) => a.type === "send_email_ab");

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest sent · ${data.moves} moves recommended`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDigestSending(false); }
  };

  const generateSequence = async () => {
    if (!seqForm.goal.trim()) { toast.error("Describe the goal in one line"); return; }
    setSeqGenerating(true);
    setSeqPreview(null);
    try {
      const { data } = await api.post("/workflows/ai-sequence", seqForm);
      setSeqPreview(data);
      toast.success(`Sequence drafted · workflow created (disabled)`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeqGenerating(false); }
  };
  const openSequenceWorkflow = () => {
    if (seqPreview?.workflow_id) navigate(`/automations/${seqPreview.workflow_id}`);
  };

  const openStats = async (wf) => {
    setStatsWf(wf);
    setStats(null);
    try {
      const { data } = await api.get(`/workflows/${wf.id}/variant-stats`);
      setStats(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="automations-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Automations</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Workflows</h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm">When X happens, do Y. Fires automatically on contact/deal/funnel events.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="ai-sequence-btn" onClick={() => { setSeqOpen(true); setSeqPreview(null); }} className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase tracking-widest px-4 py-3 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> AI Sequence
          </button>
          <button data-testid="new-workflow-btn" onClick={create} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New workflow
          </button>
        </div>
      </div>

      <div className="brutalist-card p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="growth-digest-banner">
        <div className="flex items-center gap-3 min-w-0">
          <Mail className="w-5 h-5 text-[var(--peak-blue-deep)] shrink-0" />
          <div className="min-w-0">
            <div className="section-label">Weekly Growth Digest</div>
            <div className="text-sm text-[var(--peak-muted)]">Every Monday 09:00 UTC — top performers, needs-attention, and 3 one-click AI moves in your inbox.</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/digest/preview`} target="_blank" rel="noopener noreferrer" data-testid="preview-digest-btn" className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-3 py-2 inline-flex items-center gap-1">
            <BarChart3 className="w-3 h-3" /> Preview
          </a>
          <button onClick={sendDigestNow} disabled={digestSending} data-testid="send-digest-btn" className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-3 py-2 inline-flex items-center gap-1 disabled:opacity-60">
            <Send className="w-3 h-3" /> {digestSending ? "Sending…" : "Send to me now"}
          </button>
        </div>
      </div>

      <div className="brutalist-card p-5 mb-6" data-testid="auto-triage-banner">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Cpu className={`w-5 h-5 shrink-0 ${autoTriage ? "text-[#008A27]" : "text-[var(--peak-muted)]"}`} />
            <div className="min-w-0">
              <div className="section-label inline-flex items-center gap-2">
                Auto-triage on contact create
                <span className={`text-[10px] font-mono px-1.5 py-0.5 ${autoTriage ? "bg-[#008A27] text-white" : "bg-[var(--peak-surface-alt)] text-[var(--peak-muted)]"}`}>{autoTriage ? "ON" : "OFF"}</span>
              </div>
              <div className="text-sm text-[var(--peak-muted)]">
                When a new contact lands, Claude picks the best matching AI Sequence from your enabled library — or drafts a bespoke one for them — and enrols them instantly.
              </div>
            </div>
          </div>
          <button onClick={toggleAutoTriage} disabled={triageBusy} data-testid="toggle-auto-triage" className={`font-bold uppercase text-xs tracking-widest px-4 py-2 disabled:opacity-60 ${autoTriage ? "border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white" : "bg-[var(--peak-blue-deep)] hover:bg-black text-white"}`}>
            {triageBusy ? "Saving…" : (autoTriage ? "Turn off" : "Turn on")}
          </button>
        </div>
        {autoTriage && (
          <div className="mt-4 pt-4 border-t border-[var(--peak-border)]" data-testid="confidence-dial">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="section-label">Confidence dial · park low-confidence decisions</div>
                <div className="text-xs text-[var(--peak-muted)] mt-0.5">
                  {triageThreshold === 0
                    ? "Threshold OFF — Peak sends every match/generation immediately."
                    : `Threshold ${triageThreshold}% — decisions below this are parked in the Dashboard triage feed for your approval.`}
                </div>
              </div>
              <span className="text-2xl font-heading font-black tracking-tight" data-testid="threshold-value">{triageThreshold}%</span>
            </div>
            <input
              type="range" min="0" max="100" step="5"
              value={triageThreshold}
              onChange={(e) => setTriageThreshold(Number(e.target.value))}
              onMouseUp={(e) => saveThreshold(Number(e.target.value))}
              onTouchEnd={(e) => saveThreshold(Number(e.target.value))}
              className="w-full mt-3 accent-[var(--peak-ink)]"
              data-testid="threshold-slider"
            />
            <div className="flex justify-between text-[10px] font-mono text-[var(--peak-muted)] mt-1">
              <span>0 · autopilot</span><span>50 · balanced</span><span>100 · always ask</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workflows.length === 0 && (
          <div className="col-span-full brutalist-card p-10 text-center">
            <Zap className="w-6 h-6 mx-auto text-[var(--peak-accent)]" />
            <div className="font-heading font-bold text-2xl mt-2">No workflows yet</div>
            <div className="section-label mt-1">Try "When a lead submits a funnel → send a welcome email"</div>
          </div>
        )}
        {workflows.map((wf) => (
          <div key={wf.id} className="brutalist-card p-6" data-testid={`workflow-card-${wf.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className={`section-label ${wf.enabled ? "text-[#008A27]" : ""}`}>{wf.enabled ? "Enabled" : "Draft"}</div>
                <div className="font-heading font-bold text-xl tracking-tight mt-1">{wf.name}</div>
                <div className="section-label mt-2">Trigger · {wf.trigger?.type}</div>
                <div className="section-label mt-1">Actions · {(wf.actions || []).map((a) => a.type).join(", ") || "—"}</div>
              </div>
              <button onClick={() => remove(wf.id)} data-testid={`delete-wf-${wf.id}`} className="hover:text-[#E50000]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Link to={`/automations/${wf.id}`} data-testid={`edit-wf-${wf.id}`} className="bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-bold uppercase tracking-widest text-xs px-3 py-2">Edit</Link>
              <Link to={`/automations/${wf.id}/report`} data-testid={`report-wf-${wf.id}`} className="border border-[var(--peak-blue-deep)] text-[var(--peak-blue-deep)] hover:bg-[var(--peak-blue-deep)] hover:text-white font-bold uppercase tracking-widest text-xs px-3 py-2 inline-flex items-center gap-1">
                <BarChart3 className="w-3 h-3" /> Report
              </Link>
              <button onClick={() => toggle(wf)} data-testid={`toggle-wf-${wf.id}`} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase tracking-widest text-xs px-3 py-2">
                {wf.enabled ? "Disable" : "Enable"}
              </button>
              {hasAB(wf) && (
                <button onClick={() => openStats(wf)} data-testid={`ab-stats-${wf.id}`} className="border border-[var(--peak-blue-deep)] text-[var(--peak-blue-deep)] hover:bg-[var(--peak-blue-deep)] hover:text-white font-bold uppercase tracking-widest text-xs px-3 py-2 inline-flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" /> A/B stats
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!statsWf} onOpenChange={(o) => !o && setStatsWf(null)}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none max-w-lg" data-testid="ab-stats-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[var(--peak-blue-deep)]" /> A/B RESULTS
            </DialogTitle>
          </DialogHeader>
          <div className="section-label">{statsWf?.name}</div>
          {!stats && <div className="mt-4 text-sm text-[var(--peak-muted)]">Loading…</div>}
          {stats && (
            <div className="mt-4 space-y-3">
              {stats.variants.length === 0 && (
                <div className="text-sm text-[var(--peak-muted)]">No sends yet. Enable the workflow and trigger it to gather data.</div>
              )}
              {stats.variants.length > 0 && (
                <div className="section-label">Winner selected by · <span className="text-[var(--peak-ink)] normal-case tracking-normal">{stats.winner_metric?.replace('_', ' ') || "sent"}</span></div>
              )}
              {stats.variants.map((v) => {
                const isWinner = stats.winner === v.variant;
                const openPct = Math.round((v.open_rate || 0) * 100);
                const clickPct = Math.round((v.click_rate || 0) * 100);
                return (
                  <div key={v.variant} className={`border ${isWinner ? "border-[#008A27]" : "border-[var(--peak-border)]"} p-3`} data-testid={`variant-row-${v.variant}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-bold text-lg tracking-tight">
                        Variant {v.variant}
                        {isWinner && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-[#008A27] text-white px-2 py-0.5">WINNER</span>}
                      </div>
                      <div className="section-label">{v.sent} sent · {v.delivered} delivered</div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <div className="section-label mb-1">Open rate</div>
                        <div className="h-2 bg-[var(--peak-surface-alt)] relative overflow-hidden">
                          <div className={`h-full ${isWinner && stats.winner_metric === 'open_rate' ? 'bg-[#008A27]' : 'bg-[var(--peak-blue-deep)]'}`} style={{ width: `${Math.min(100, openPct)}%` }} />
                        </div>
                        <div className="font-heading font-bold text-lg tracking-tighter mt-1">{openPct}%</div>
                        <div className="section-label">{v.opened} opens</div>
                      </div>
                      <div>
                        <div className="section-label mb-1">Click rate</div>
                        <div className="h-2 bg-[var(--peak-surface-alt)] relative overflow-hidden">
                          <div className={`h-full ${isWinner && stats.winner_metric === 'click_rate' ? 'bg-[#008A27]' : 'bg-[var(--peak-accent)]'}`} style={{ width: `${Math.min(100, clickPct)}%` }} />
                        </div>
                        <div className="font-heading font-bold text-lg tracking-tighter mt-1">{clickPct}%</div>
                        <div className="section-label">{v.clicked} clicks</div>
                      </div>
                    </div>

                    {(v.bounced > 0 || v.failed > 0) && (
                      <div className="text-xs text-[#E50000] mt-2">
                        {v.bounced > 0 && <span>{v.bounced} bounced </span>}
                        {v.failed > 0 && <span>{v.failed} failed</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Sequence Starter modal */}
      {seqOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ai-sequence-modal" onClick={() => !seqGenerating && setSeqOpen(false)}>
          <div className="brutalist-card p-6 w-full max-w-3xl bg-white max-h-[92vh] overflow-y-auto tick-scroll" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="section-label">AI Sequence starter</div>
                <h2 className="font-heading font-black text-2xl tracking-tighter uppercase mt-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[var(--peak-blue-deep)]" /> 3-email nurture</h2>
                <p className="text-sm text-[var(--peak-muted)] mt-1">Claude drafts Day&nbsp;0 · Day&nbsp;3 · Day&nbsp;7 emails and creates a disabled workflow you can enable in one click.</p>
              </div>
              <button onClick={() => !seqGenerating && setSeqOpen(false)} data-testid="seq-close-btn" className="p-1.5 border border-[var(--peak-border)] hover:border-[#E50000] hover:text-[#E50000]"><X className="w-4 h-4" /></button>
            </div>

            {!seqPreview && (
              <>
                <label className="section-label">Goal (one line)</label>
                <input className="brutalist-input" type="text" placeholder="e.g. book a 15-min demo call about workflow automation"
                  value={seqForm.goal} onChange={(e) => setSeqForm({ ...seqForm, goal: e.target.value })} data-testid="seq-goal" />
                <label className="section-label mt-3">Audience <span className="text-[var(--peak-muted)] normal-case tracking-normal">(optional)</span></label>
                <input className="brutalist-input" type="text" placeholder="e.g. newly signed-up leads from marketing funnel"
                  value={seqForm.audience} onChange={(e) => setSeqForm({ ...seqForm, audience: e.target.value })} data-testid="seq-audience" />
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="section-label">Tone</label>
                    <input className="brutalist-input" type="text" value={seqForm.tone} onChange={(e) => setSeqForm({ ...seqForm, tone: e.target.value })} data-testid="seq-tone" />
                  </div>
                  <div>
                    <label className="section-label">Workflow name <span className="text-[var(--peak-muted)] normal-case tracking-normal">(optional)</span></label>
                    <input className="brutalist-input" type="text" placeholder="AI Sequence · {goal}" value={seqForm.name} onChange={(e) => setSeqForm({ ...seqForm, name: e.target.value })} data-testid="seq-name" />
                  </div>
                </div>
                <div className="mt-5 flex gap-2 flex-wrap justify-end">
                  <button onClick={() => setSeqOpen(false)} disabled={seqGenerating} className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2">Cancel</button>
                  <button onClick={generateSequence} disabled={seqGenerating || !seqForm.goal.trim()} data-testid="seq-generate-btn" className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60">
                    <Sparkles className="w-3 h-3" /> {seqGenerating ? "Drafting the sequence…" : "Draft with Claude"}
                  </button>
                </div>
              </>
            )}

            {seqPreview && (
              <div data-testid="seq-preview">
                <div className="section-label inline-flex items-center gap-1"><Zap className="w-3 h-3 text-[#008A27]" /> Workflow created (disabled)</div>
                <div className="font-heading font-bold text-lg mt-1">{seqPreview.name}</div>
                <div className="mt-4 space-y-3">
                  {seqPreview.emails.map((e) => (
                    <div key={e.day} className={`border-l-4 p-3 ${e.day === 0 ? "border-[var(--peak-accent)] bg-[#FFF7F3]" : e.day === 3 ? "border-[var(--peak-blue-deep)] bg-[#F5F7FF]" : "border-[var(--peak-ink)] bg-[var(--peak-surface-alt)]"}`} data-testid={`seq-email-day-${e.day}`}>
                      <div className="section-label">Day {e.day}</div>
                      <div className="font-bold mt-1">{e.subject}</div>
                      <div className="text-sm whitespace-pre-wrap mt-2 text-[var(--peak-ink)]">{e.body}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex gap-2 flex-wrap justify-end">
                  <button onClick={() => { setSeqPreview(null); setSeqForm({ goal: "", audience: "", tone: "warm and direct", name: "" }); }} className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-4 py-2" data-testid="seq-draft-another">
                    Draft another
                  </button>
                  <button onClick={openSequenceWorkflow} data-testid="seq-open-btn" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1">
                    <Send className="w-3 h-3" /> Open workflow to enable
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
